import { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { openExternalBrowser } from "%/externalBrowser";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

interface Props {
  url: string;
  hash?: string | null;
  title?: string;
}

export default function FinalContractPdfViewer({
  url,
  hash,
  title = "체결 완료본",
}: Props) {
  const canvasRootRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "fallback">(
    "loading",
  );
  const pdfUrl = `${url}#toolbar=0&navpanes=0&scrollbar=1`;

  useEffect(() => {
    let cancelled = false;
    const root = canvasRootRef.current;
    if (!root) return;

    root.innerHTML = "";
    setStatus("loading");

    const renderPdf = async () => {
      try {
        const loadingTask = pdfjsLib.getDocument({ url });
        const pdf = await loadingTask.promise;
        if (cancelled) {
          await pdf.destroy();
          return;
        }

        for (let pageNo = 1; pageNo <= pdf.numPages; pageNo += 1) {
          const page = await pdf.getPage(pageNo);
          if (cancelled) break;

          const viewport = page.getViewport({ scale: 1.6 });
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          if (!context) continue;

          canvas.width = Math.ceil(viewport.width);
          canvas.height = Math.ceil(viewport.height);
          canvas.className = "block h-auto w-full bg-white shadow-sm";
          await page.render({ canvas, canvasContext: context, viewport }).promise;

          if (!cancelled) {
            root.appendChild(canvas);
          }
        }

        await pdf.destroy();
        if (!cancelled) setStatus("ready");
      } catch {
        if (!cancelled) setStatus("fallback");
      }
    };

    void renderPdf();

    return () => {
      cancelled = true;
      root.innerHTML = "";
    };
  }, [url]);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[14px] font-semibold text-[#222]">{title}</div>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          onClick={(event) => {
            event.preventDefault();
            openExternalBrowser(url);
          }}
          className="shrink-0 rounded-lg bg-[#FF9326] px-3 py-2 text-[12px] font-semibold text-white"
        >
          PDF 열기
        </a>
      </div>
      <div className="overflow-hidden rounded-lg border border-[#E5E5E5] bg-white">
        {status === "loading" && (
          <div className="flex h-[320px] items-center justify-center text-[13px] text-[#888]">
            계약서를 불러오는 중입니다.
          </div>
        )}
        <div
          ref={canvasRootRef}
          className={`flex flex-col gap-4 bg-[#F4F4F4] p-3 ${
            status === "fallback" ? "hidden" : ""
          }`}
        />
        {status === "fallback" && (
          <object
            data={pdfUrl}
            type="application/pdf"
            className="h-[72vh] min-h-[520px] w-full"
            aria-label={title}
          >
            <iframe
              src={pdfUrl}
              title={title}
              className="h-[72vh] min-h-[520px] w-full"
            />
          </object>
        )}
      </div>
      {hash && (
        <div className="break-all text-[10px] text-[#AAA]">SHA-256: {hash}</div>
      )}
    </section>
  );
}
